import { useState } from 'react';
import { format, isSameDay, startOfDay } from 'date-fns';
// @mui
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  useTheme,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

export default function MeetingCalendar({ 
  availableDates, 
  selectedDate, 
  onDateSelect,
  minDate = new Date()
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // 월 이동
  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  // 달력 날짜 생성
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  // 날짜가 선택 가능한지 확인
  const isDateAvailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availableDates.includes(dateStr);
  };

  // 날짜가 현재 월에 속하는지 확인
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  // 날짜가 오늘 이전인지 확인
  const isPastDate = (date) => {
    return startOfDay(date) < startOfDay(minDate);
  };

  const handleDateClick = (date) => {
    if (isDateAvailable(date) && !isPastDate(date)) {
      const dateStr = format(date, 'yyyy-MM-dd');
      onDateSelect(dateStr);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: { xs: 2, sm: 3 },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      {/* 월 네비게이션 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
      }}>
        <IconButton onClick={handlePrevMonth} size="small">
          <ChevronLeft />
        </IconButton>

        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {format(currentMonth, 'yyyy년 MM월')}
        </Typography>

        <IconButton onClick={handleNextMonth} size="small">
          <ChevronRight />
        </IconButton>
      </Box>

      {/* 요일 헤더 */}
      <Grid container spacing={0} sx={{ mb: 1 }}>
        {weekDays.map((day, index) => (
          <Grid item xs={12/7} key={day}>
            <Box sx={{ 
              textAlign: 'center',
              py: 1,
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 600,
                  color: index === 0 ? theme.palette.error.main : 
                         index === 6 ? theme.palette.info.main : 
                         theme.palette.text.secondary,
                }}
              >
                {day}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* 날짜 그리드 */}
      <Grid container spacing={0.5}>
        {calendarDays.map((date, index) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isAvailable = isDateAvailable(date);
          const isSelected = selectedDate === dateStr;
          const isInCurrentMonth = isCurrentMonth(date);
          const isPast = isPastDate(date);
          const isToday = isSameDay(date, new Date());

          return (
            <Grid item xs={12/7} key={index}>
              <Box
                onClick={() => handleDateClick(date)}
                sx={{
                  aspectRatio: isMobile ? '1/1' : '1.2/1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  borderRadius: 1,
                  cursor: isAvailable && !isPast ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  border: isToday ? `1px solid ${theme.palette.info.lighter}` : 'none',
                  bgcolor: isSelected ? theme.palette.info.main :
                          isAvailable && !isPast ? alpha(theme.palette.info.main, 0.16) :
                          'transparent',
                  opacity: !isInCurrentMonth ? 0.6 : 1,
                  '&:hover': {
                    bgcolor: isAvailable && !isPast && !isSelected ? 
                            alpha(theme.palette.info.main, 0.08) : undefined,
                  },
                }}
              >
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{
                    fontWeight: isSelected || isToday ? 600 : 400,
                    color: isSelected ? theme.palette.common.white :
                           isPast ? theme.palette.text.disabled :
                           !isAvailable ? theme.palette.text.disabled :
                           date.getDay() === 0 ? theme.palette.error.main :
                           date.getDay() === 6 ? theme.palette.info.main :
                           theme.palette.text.primary,
                    ...(!isAvailable && ({ textDecoration: 'line-through' })),
                  }}
                >
                  {format(date, 'd')}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
}
